import { Link, useParams } from 'react-router-dom'
import { paths } from '../../app/routes'
import { provisionalRawScore } from '../../offline/evaluate'
import styles from './ResultScreen.module.css'
import { useSessionResult } from './useSessionResult'

/**
 * The result screen: provisional versus authoritative result, including the
 * percentile-pending state.
 *
 * This screen is where the rule that local evaluation is only provisional
 * either holds or fails visibly. Offline it may show
 * per-item correct/incorrect and a **raw** score labelled provisional. It must
 * never show a percentile before the server has produced one — a number that
 * later changes destroys trust in the scoring, and §5.3 makes the server's
 * result *replace* the local one rather than merge with it.
 */
export function ResultScreen() {
  const { sessionId = '' } = useParams()
  const { session, responses, online, waiting, settling } = useSessionResult(sessionId)

  if (!session) return <p className="muted">Načítám výsledek…</p>

  const provisional = provisionalRawScore(responses.map((r) => r.localResult))
  const authoritative = session.serverResult

  return (
    <div className="stack">
      <h1>Výsledek</h1>

      {authoritative ? (
        <section className="card">
          <p className={styles.score}>
            {authoritative.rawScore} / {authoritative.maxScore}
          </p>
          <p className={styles.percentile}>
            Percentil <strong>{authoritative.percentile}</strong>
          </p>
          <p className="muted small">Vyhodnoceno serverem — toto je platný výsledek.</p>
          {authoritative.durationOverrunFlagged ? (
            <p className="muted small">Čas byl překročen nad rámec tolerance; výsledek je označen ke kontrole.</p>
          ) : null}
        </section>
      ) : (
        <section className={`card ${styles.provisional}`}>
          <p className={styles.score}>
            {provisional.rawScore} / {provisional.scored}
          </p>
          <p className="tag">předběžný výsledek</p>
          {/*
            The percentile-pending state, spelled out. Only the server computes
            a percentile, so the honest thing is to say when it will appear
            rather than to show a placeholder number.
          */}
          <p className="muted">
            Percentil se zobrazí po odeslání odpovědí na server. Zatím se počítá jen počet správných odpovědí.
          </p>
          <p className="muted small">
            {settling
              ? 'Odesílám…'
              : online
                ? `Čeká na odeslání: ${waiting}`
                : 'Jste offline. Odpovědi se odešlou při dalším připojení nebo při dalším otevření aplikace.'}
          </p>
        </section>
      )}

      <section className="stack">
        <h2 className="section-title">Jednotlivé úlohy</h2>
        <ul className="rows">
          {responses.map((response) => {
            const serverVerdict = authoritative?.perItem.find((p) => p.itemId === response.itemId)
            const verdict = serverVerdict?.correct ?? (response.localResult === 'correct')
            return (
              <li key={response.id} className="row">
                <code>{response.itemId}</code>
                <span className={verdict ? 'ok' : 'bad'}>{verdict ? 'správně' : 'chybně'}</span>
                <span className="muted small">{serverVerdict ? 'server' : 'předběžné'}</span>
                {/* Version conflict made visible: the item changed between prefetch and sync. */}
                {(serverVerdict?.versionConflict ?? response.versionConflict) ? (
                  <span className="tag tag--warn">úloha byla mezitím upravena</span>
                ) : null}
              </li>
            )
          })}
        </ul>
      </section>

      <Link className="button" to={paths.home}>
        Zpět na přehled
      </Link>
    </div>
  )
}
